import React, { useState, useEffect, useCallback } from 'react';
import { Signpost, Loader2, Download, Trash2, Save, Phone, Mail, MapPin, ShieldCheck, RefreshCw, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '../../components/ui/alert-dialog';
import { api } from '../../lib/api';
import { formatDate } from '../../components/Shared';

/* Admin -> Sign Requests (Phase 123)
   One list for every lawn sign request, whether it came from /request-a-sign
   or from the Contact form with "Request a Sign" ticked. Contact details are
   admin-only and never appear on any public page. */

const STATUS_LABELS = { new: 'New', contacted: 'Contacted', placed: 'Placed', declined: 'Declined', picked_up: 'Picked up' };
const STATUS_STYLES = {
  new: 'bg-[#EAF2FF] text-[#1a2744] border-[#c7d7f5]',
  contacted: 'bg-amber-50 text-amber-900 border-amber-200',
  placed: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  declined: 'bg-slate-100 text-slate-600 border-slate-200',
  picked_up: 'bg-slate-100 text-slate-700 border-slate-300',
};

const RequestCard = ({ item, statuses, onSaved, onDelete }) => {
  const [status, setStatus] = useState(item.status);
  const [note, setNote] = useState(item.admin_note || '');
  const [busy, setBusy] = useState(false);
  const dirty = status !== item.status || note !== (item.admin_note || '');

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/admin/sign-requests/${item.id}`, { status, admin_note: note });
      onSaved(r.data);
      toast.success('Sign request updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally { setBusy(false); }
  };

  return (
    <Card data-testid={`sign-request-card-${item.id}`}>
      <CardContent className="p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-bold text-[#1a2744] text-lg leading-tight" data-testid="sign-request-name">{item.name}</p>
            <p className="text-sm text-slate-700 mt-1 inline-flex items-start gap-1.5" data-testid="sign-request-address"><MapPin className="h-4 w-4 mt-0.5 shrink-0 text-[#b91c1c]" /> {item.address}</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-sm text-slate-600">
              {item.phone && <a href={`tel:${item.phone}`} className="inline-flex items-center gap-1 hover:underline" data-testid="sign-request-phone"><Phone className="h-3.5 w-3.5" /> {item.phone}</a>}
              {item.email && <a href={`mailto:${item.email}`} className="inline-flex items-center gap-1 hover:underline" data-testid="sign-request-email"><Mail className="h-3.5 w-3.5" /> {item.email}</a>}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5 text-right">
            <Badge className={`${STATUS_STYLES[item.status] || ''} border`} data-testid="sign-request-status-badge">{STATUS_LABELS[item.status] || item.status}</Badge>
            <p className="text-[11px] text-slate-500">Ref {item.id.slice(0, 8).toUpperCase()} · {formatDate(item.created_at)}</p>
            <div className="flex flex-wrap justify-end gap-1">
              {(item.sources || []).map((s) => <Badge key={s} variant="outline" className="text-[10px]" data-testid={`sign-request-source-${s}`}>{s === 'contact-form' ? 'via Contact form' : 'via Request a Sign'}</Badge>)}
              {item.repeat_count > 0 && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-800" data-testid="sign-request-repeat">asked {item.repeat_count + 1} times</Badge>}
            </div>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
            <p className="text-[11px] font-black uppercase tracking-wide text-slate-500">Sign size</p>
            <p className="font-semibold text-[#1a2744] mt-0.5" data-testid="sign-request-size">{item.size_label || item.size}</p>
            <p className={`text-xs mt-1 inline-flex items-center gap-1 ${item.permission_confirmed ? 'text-emerald-700' : 'text-red-700'}`}><ShieldCheck className="h-3.5 w-3.5" /> {item.permission_confirmed ? 'Permission confirmed' : 'Permission NOT confirmed'}</p>
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
            <p className="text-[11px] font-black uppercase tracking-wide text-slate-500">Resident notes</p>
            <p className="text-slate-700 mt-0.5 whitespace-pre-line" data-testid="sign-request-notes">{item.notes || 'None'}</p>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-[180px_1fr_auto] gap-2 items-start">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-10" data-testid={`sign-request-status-select-${item.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {statuses.map((s) => <SelectItem key={s} value={s} data-testid={`sign-request-status-option-${s}`}>{STATUS_LABELS[s] || s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={1} placeholder="Admin note (volunteer assigned, date placed, etc.)" className="min-h-[40px]" data-testid={`sign-request-note-${item.id}`} />
          <div className="flex gap-1.5">
            <Button onClick={save} disabled={!dirty || busy} className="h-10 bg-[#1a2744] hover:bg-[#24406f] text-white font-bold" data-testid={`sign-request-save-${item.id}`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}<span className="ml-1.5 hidden sm:inline">Save</span>
            </Button>
            <Button variant="outline" onClick={() => onDelete(item)} className="h-10 border-red-200 text-red-700 hover:bg-red-50" aria-label="Delete request" data-testid={`sign-request-delete-${item.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const OptionsEditor = ({ options, onSaved }) => {
  const [sizes, setSizes] = useState(options.sizes || []);
  const [intro, setIntro] = useState(options.intro || '');
  const [privacy, setPrivacy] = useState(options.privacy_note || '');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setSizes(options.sizes || []); setIntro(options.intro || ''); setPrivacy(options.privacy_note || ''); }, [options]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put('/admin/sign-requests-options', { sizes, intro, privacy_note: privacy });
      onSaved(r.data);
      toast.success('Sign form options saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save options');
    } finally { setBusy(false); }
  };

  return (
    <Card data-testid="sign-options-editor">
      <CardContent className="p-4 sm:p-5 space-y-3">
        <p className="font-bold text-[#1a2744]">Public form wording and sign sizes</p>
        <div>
          <label className="block text-[11px] font-bold text-slate-500 mb-1">Intro sentence</label>
          <Input value={intro} onChange={(e) => setIntro(e.target.value)} className="h-10" data-testid="sign-options-intro" />
        </div>
        <div>
          <label className="block text-[11px] font-bold text-slate-500 mb-1">Privacy note</label>
          <Input value={privacy} onChange={(e) => setPrivacy(e.target.value)} className="h-10" data-testid="sign-options-privacy" />
        </div>
        <div>
          <label className="block text-[11px] font-bold text-slate-500 mb-1">Sign sizes (key, label)</label>
          <div className="space-y-1.5">
            {sizes.map((s, i) => (
              <div key={i} className="grid grid-cols-[1fr_2fr_auto] gap-1.5">
                <Input value={s.key} onChange={(e) => setSizes(sizes.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)))} className="h-9 text-sm font-mono" data-testid={`sign-options-size-key-${i}`} />
                <Input value={s.label} onChange={(e) => setSizes(sizes.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} className="h-9 text-sm" data-testid={`sign-options-size-label-${i}`} />
                <Button variant="ghost" size="sm" className="h-9 w-9 p-0 text-red-600" onClick={() => setSizes(sizes.filter((_, j) => j !== i))} aria-label="Remove size"><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" className="h-9 text-xs font-bold border-dashed" onClick={() => setSizes([...sizes, { key: '', label: '' }])} data-testid="sign-options-add-size"><Plus className="h-3.5 w-3.5 mr-1" /> Add size</Button>
          </div>
        </div>
        <Button onClick={save} disabled={busy} className="h-10 bg-[#1a2744] hover:bg-[#24406f] text-white font-bold" data-testid="sign-options-save">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />} Save options</Button>
      </CardContent>
    </Card>
  );
};

export default function AdminSignRequests() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/sign-requests', { params: { status: filter } });
      setData(r.data);
    } catch (e) {
      toast.error('Could not load sign requests');
    } finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const confirmDelete = async () => {
    const item = pendingDelete;
    setPendingDelete(null);
    try {
      await api.delete(`/admin/sign-requests/${item.id}`);
      toast.success('Sign request deleted');
      load();
    } catch (e) {
      toast.error('Could not delete');
    }
  };

  const total = data ? Object.values(data.counts || {}).reduce((a, b) => a + b, 0) : 0;

  // Authenticated download (Bearer token travels with axios, not with <a href>)
  const exportCsv = async () => {
    try {
      const r = await api.get('/admin/sign-requests/export', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'sign-requests.csv'; document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Could not export');
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-sign-requests-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-serif font-black text-2xl text-[#1a2744] inline-flex items-center gap-2"><Signpost className="h-6 w-6" /> Sign Requests</h1>
          <p className="text-sm text-slate-600 mt-1">Every lawn sign request in one list, from the Request a Sign page and from the Contact form. Same person and address within 30 days updates one record instead of creating a duplicate.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} className="h-10 font-bold" data-testid="sign-requests-refresh"><RefreshCw className="h-4 w-4 mr-1.5" /> Refresh</Button>
          <Button variant="outline" onClick={exportCsv} className="h-10 font-bold" data-testid="sign-requests-export"><Download className="h-4 w-4 mr-1.5" /> Export CSV</Button>
        </div>
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="flex flex-wrap h-auto gap-1" data-testid="sign-requests-filter">
          <TabsTrigger value="all" data-testid="sign-requests-filter-all">All ({total})</TabsTrigger>
          {(data?.statuses || Object.keys(STATUS_LABELS)).map((s) => (
            <TabsTrigger key={s} value={s} data-testid={`sign-requests-filter-${s}`}>{STATUS_LABELS[s] || s} ({data?.counts?.[s] ?? 0})</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {loading && !data ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-40 rounded-xl" />)}</div>
      ) : (data?.items || []).length === 0 ? (
        <Card><CardContent className="p-10 text-center text-slate-500" data-testid="sign-requests-empty">
          <Signpost className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          <p className="font-bold text-[#1a2744]">No sign requests {filter !== 'all' ? `with status "${STATUS_LABELS[filter] || filter}"` : 'yet'}</p>
          <p className="text-sm mt-1">Requests from /request-a-sign and the Contact form will appear here.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3" data-testid="sign-requests-list">
          {data.items.map((item) => (
            <RequestCard key={item.id} item={item} statuses={data.statuses || Object.keys(STATUS_LABELS)}
              onSaved={(u) => setData((d) => ({ ...d, items: d.items.map((x) => (x.id === u.id ? u : x)) }))}
              onDelete={setPendingDelete} />
          ))}
        </div>
      )}

      {data?.options && <OptionsEditor options={data.options} onSaved={(o) => setData((d) => ({ ...d, options: o }))} />}

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent data-testid="sign-request-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this sign request?</AlertDialogTitle>
            <AlertDialogDescription>{pendingDelete ? `${pendingDelete.name}, ${pendingDelete.address}. This cannot be undone. Tip: mark it Declined or Picked up instead if you want to keep the record.` : ''}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sign-request-delete-cancel">Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-700 hover:bg-red-800" data-testid="sign-request-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
